import type { OpenAPIResponse, OpenAPIVoidResponse } from '../../api/api-switcher'
import { toApiResultAsync, type ApiResult } from '../../api/api-error'
import type { Album, Photo, ReactionToPhoto, Share, Tags, TagsToPhoto } from './types'

export type AlbumCacheProps<T extends Share | Album> = {
  getAlbum: (signal?: AbortSignal)=> OpenAPIResponse<T>
  getPhoto: (id: string) => OpenAPIResponse<Photo>
  tagsToPhoto: TagsToPhoto
  reactionToPhoto: ReactionToPhoto
  removePhotos: (ids: string[]) => OpenAPIVoidResponse
}
type PhotoResource = {
  // Reactのuse()へ毎回同じPromiseを渡しつつ、更新APIから表示中の写真も変更できるよう
  // Promiseとその解決値への参照を同じリソースとして管理する。
  promise: Promise<ApiResult<Photo>>
  photo?: Photo
}
export class AlbumCache<T extends Share | Album> {
  #album: T | undefined = undefined
  #album_promise: Promise<ApiResult<T>> | undefined = undefined
  #last_successful_album_promise: Promise<ApiResult<T>> | undefined = undefined
  readonly #photo_resources = new Map<string, PhotoResource>()
  readonly #getAlbum: (signal?: AbortSignal)=> OpenAPIResponse<T>
  readonly #getPhoto: (id: string) => OpenAPIResponse<Photo>
  readonly #tagsToPhoto: TagsToPhoto
  readonly #reactionToPhoto: ReactionToPhoto
  readonly #removePhotos: (ids: string[]) => OpenAPIVoidResponse
  constructor ({getAlbum, removePhotos, getPhoto, tagsToPhoto, reactionToPhoto}: AlbumCacheProps<T>) {
    this.#getAlbum = getAlbum
    this.#removePhotos = removePhotos
    this.#getPhoto = getPhoto
    this.#tagsToPhoto = tagsToPhoto
    this.#reactionToPhoto = reactionToPhoto
  }
  getAlbum (reload = false): Promise<ApiResult<T>> {
    if (!this.#album_promise || reload) {
      const album_promise = toApiResultAsync(() => this.#getAlbum()).then(result => {
        // reloadが並行した場合、後から開始した取得だけがキャッシュを更新する。
        if (this.#album_promise === album_promise) {
          if (result.data) {
            this.#album = result.data
            this.#last_successful_album_promise = album_promise
            // パスワード認証前の403/404や古い写真を次の描画で再利用しない。
            if (reload) this.#photo_resources.clear()
          } else if (reload && this.#last_successful_album_promise) {
            // reloadの呼び出し元には失敗を返すが、既存画面は最後の成功結果を読み続けられるようにする。
            this.#album_promise = this.#last_successful_album_promise
          }
        }
        return result
      })
      this.#album_promise = album_promise
    }
    return this.#album_promise
  }
  getPhoto (id: string): Promise<ApiResult<Photo>> {
    const cached = this.#photo_resources.get(id)
    if (cached) return cached.promise

    // アルバム取得済みなら同じ写真オブジェクトを使い、詳細APIの重複呼び出しを避ける。
    const album_photo = this.#album?.photos.find((p): p is Photo => 'photo_id' in p && p.photo_id === id)
    const fetch_promise = album_photo
      ? Promise.resolve({data: album_photo, error: undefined, response: new Response()} as ApiResult<Photo>)
      : toApiResultAsync(() => this.#getPhoto(id))
    const resource: PhotoResource = {promise: fetch_promise}
    resource.promise = fetch_promise.then(result => {
      // 削除・reload後に古いPromiseが完了しても、破棄済みリソースを復活させない。
      if (this.#photo_resources.get(id) === resource && result.data) resource.photo = result.data
      return result
    })
    this.#photo_resources.set(id, resource)
    return resource.promise
  }
  async tagsToPhoto (id: string, tags: Tags): OpenAPIResponse<Omit<Photo, 'added'>> {
    const {data: new_photo, error, response} = await this.#tagsToPhoto(id, tags)
    if (error) return {data: undefined, error, response}
    if (this.#album) {
      const photo = this.#album.photos.find(p=> 'photo_id' in p && p.photo_id === id)
      if (photo) Object.assign(photo, new_photo)
    }
    // albumと写真詳細は並列取得されるため、両者が別オブジェクトの場合は両方を同期する。
    const resource_photo = this.#photo_resources.get(id)?.photo
    if (resource_photo) Object.assign(resource_photo, new_photo)
    return {data: new_photo, error: undefined, response}
  }
  async reactionToPhoto (id: string, reaction: string, add: boolean): OpenAPIResponse<Photo> {
    const {data: new_photo, error, response} = await this.#reactionToPhoto(id, reaction, add)
    if (error) return {data: undefined, error, response}
    if (this.#album) {
      const photo = this.#album.photos.find(p=> 'photo_id' in p && p.photo_id === id)
      if (photo) {
        // リアクションAPIの応答は写真全体ではないため、他のフィールドを上書きしない。
        if ('reactions' in new_photo && !('loading_key' in photo)) {
          (photo as Photo).reactions = new_photo.reactions
        } else {
          Object.assign(photo, new_photo)
        }
      }
    }
    // Viewerが写真詳細リソースを読んでいる場合も、同じオブジェクトへ更新を反映する。
    const resource_photo = this.#photo_resources.get(id)?.photo
    if (resource_photo) {
      if ('reactions' in new_photo && !('loading_key' in resource_photo)) {
        resource_photo.reactions = new_photo.reactions
      } else {
        Object.assign(resource_photo, new_photo)
      }
    }
    return {data: new_photo, error: undefined, response}
  }
  async removePhotos (ids: string[], only_cache = false): OpenAPIVoidResponse {
    if (this.#album) this.#album.photos = this.#album.photos.filter(p=> !ids.includes(p.photo_id))
    for (const id of ids) this.#photo_resources.delete(id)
    if (!only_cache) return this.#removePhotos(ids)
    return {response: new Response()}
  }
  reset () {
    this.#album = undefined
    this.#album_promise = undefined
    this.#last_successful_album_promise = undefined
    this.#photo_resources.clear()
  }
}
